import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { DotsThreeIcon } from "@phosphor-icons/react/DotsThree";
import { Icon } from "../../Icons/Icon";
import { PortalContextReset } from "../../Controls/Internal/PortalContextReset";
import { countColorClasses } from "./TabTrigger";
import type { TabCountColor } from "../Tab";

interface OverflowTab {
  id: string;
  title: string;
  count?: number | undefined;
  countColor?: TabCountColor | undefined;
  disabled?: boolean | undefined;
}

interface TabOverflowMenuProps {
  tabs: OverflowTab[];
  activeTab: string;
  onSelect: (tabId: string) => void;
  dense?: boolean | undefined;
}

export function TabOverflowMenu_({
  tabs,
  activeTab,
  onSelect,
  dense,
}: TabOverflowMenuProps) {
  if (tabs.length === 0) {
    // oxlint-disable-next-line fs/no-null -- React requires null for conditional rendering
    return null;
  }

  const hasActive = tabs.some((t) => t.id === activeTab);

  return (
    <DropdownMenuPrimitive.Root>
      <DropdownMenuPrimitive.Trigger asChild>
        <button
          type="button"
          className={`z-1 inline-flex shrink-0 cursor-pointer items-center rounded-t-lg border-2 border-b-0 font-bold whitespace-nowrap transition-colors focus:outline-none ${
            dense
              ? "gap-1 px-2.5 py-1.5 text-[12px]"
              : "gap-1.5 px-3 py-2.5 text-[13px]"
          } ${
            hasActive
              ? `relative z-[1] -mb-[2px] border-border bg-card ${dense ? "pb-[calc(0.375rem+2px)]" : "pb-[calc(0.625rem+2px)]"} text-foreground`
              : "border-transparent bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground"
          }`}
        >
          <Icon icon={DotsThreeIcon} size="sm" weight="bold" />
          <span
            className={`rounded-full text-center font-extrabold ${
              dense
                ? "min-w-[16px] px-1 py-px text-[10px] leading-tight"
                : "min-w-[20px] px-1.5 py-px text-[11px] leading-tight"
            } ${
              hasActive
                ? "bg-blue text-white"
                : "bg-foreground/8 text-muted-foreground"
            }`}
          >
            {tabs.length}
          </span>
        </button>
      </DropdownMenuPrimitive.Trigger>

      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          align="end"
          sideOffset={4}
          className="min-w-44 rounded-lg border border-border bg-card py-1 shadow-floating"
        >
          {/* Portaled content must not inherit the trigger surface's layout
              contexts — see PortalContextReset. */}
          <PortalContextReset>
            {tabs.map((tab) => (
              <DropdownMenuPrimitive.Item
                key={tab.id}
                disabled={tab.disabled === true}
                className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors outline-none ${
                  tab.disabled
                    ? "cursor-not-allowed text-content-faint"
                    : `cursor-pointer hover:bg-muted ${tab.id === activeTab ? "font-bold text-foreground" : "text-content-secondary"}`
                }`}
                onSelect={() => onSelect(tab.id)}
              >
                <span className="flex-1">{tab.title}</span>
                {tab.count !== undefined && (
                  <span
                    className={`rounded-full px-1.5 py-px text-center text-[11px] leading-tight font-extrabold ${
                      tab.id === activeTab
                        ? tab.countColor
                          ? countColorClasses[tab.countColor]
                          : "bg-blue text-white"
                        : "bg-foreground/8 text-muted-foreground"
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </DropdownMenuPrimitive.Item>
            ))}
          </PortalContextReset>
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}
